import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { taskAPI, userAPI, reopenAPI, deadlineExtensionAPI, uploadAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Card, Badge, AvatarGroup, Spinner, Button, Avatar, Modal, Textarea, PriorityIcon, NavIcon, ReasonModal, ConfirmDialog } from '../common';
import { STATUS_CONFIG, PRIORITY_CONFIG, formatDate, formatDateTime, isOverdue, resolveFileUrl, handleDownloadFile } from '../../utils/constants';
import { fetchUsersCached } from '../../utils/lookupCache';

const TrashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const InlineSelect = ({ value, onChange, disabled, children }) => {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="text-xs font-semibold bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl px-2.5 py-1 outline-none cursor-pointer focus:border-brand-500 focus:ring-2 focus:ring-brand-100/60 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
    >
      {children}
    </select>
  );
};

const StatusHistoryRow = ({ entry }) => {
  const targetStatus = entry.to || entry.status;
  const fromStatus = entry.from;
  const scfg = STATUS_CONFIG[targetStatus] || {};
  const fromCfg = STATUS_CONFIG[fromStatus] || {};
  
  const userObj = entry.changedBy || entry.user;
  const userName = userObj?.name || (typeof userObj === 'string' ? userObj : 'System');
  const dateVal = entry.changedAt || entry.updatedAt || entry.createdAt;
  const reasonVal = entry.reason || entry.note || entry.holdReason;

  return (
    <div className="relative pl-5 py-3 border-b border-slate-100/60 dark:border-slate-800/40 last:border-0">
      <span className={`absolute left-[-5px] top-4.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 ${scfg.dot || 'bg-brand-500'}`} />
      
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
          {userName}
        </span>
        <span className="text-[11px] text-slate-400 font-medium">
          {formatDateTime(dateVal)}
        </span>
      </div>

      <div className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5 flex-wrap">
        <span>Status changed</span>
        {fromStatus && fromCfg.label && (
          <>
            <span>from</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${fromCfg.color || 'bg-slate-100 text-slate-700'}`}>
              {fromCfg.label}
            </span>
          </>
        )}
        <span>to</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${scfg.color || 'bg-brand-100 text-brand-700'}`}>
          {scfg.label || targetStatus || 'Updated'}
        </span>
      </div>

      {reasonVal && (
        <p className="text-xs text-slate-500 dark:text-slate-400 italic mt-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl px-3 py-1.5 border border-slate-100 dark:border-slate-800/80">
          Reason: "{reasonVal}"
        </p>
      )}
    </div>
  );
};

const DeadlineHistoryRow = ({ entry }) => {
  const userName = entry.changedBy?.name || entry.user?.name || 'User';
  const prevDate = entry.previousDeadline || entry.oldDeadline;
  const nextDate = entry.updatedDeadline || entry.newDeadline;
  const dateStr  = entry.changedAt || entry.updatedAt;
  const reasonStr = entry.reason || entry.note;

  return (
    <div className="py-2.5 border-b border-amber-100/50 dark:border-amber-950/20 last:border-0">
      <div className="flex justify-between items-center text-xs text-amber-700 dark:text-amber-400 mb-1">
        <span className="font-semibold">{userName}</span>
        <span>{formatDate(dateStr)}</span>
      </div>
      <p className="text-sm text-slate-800 dark:text-slate-200">
        Changed deadline from{' '}
        <span className="line-through text-slate-400">{prevDate ? formatDate(prevDate) : 'No deadline'}</span>
        {' '}to{' '}
        <span className="font-semibold text-brand-600 dark:text-brand-400">{nextDate ? formatDate(nextDate) : 'Removed'}</span>
      </p>
      {reasonStr && (
        <p className="text-xs text-slate-600 dark:text-slate-400 italic mt-1 bg-amber-100/40 dark:bg-amber-900/20 px-2 py-1 rounded-lg">
          Reason: "{reasonStr}"
        </p>
      )}
    </div>
  );
};

const SubtasksSection = ({ task, setTask, isAdmin, isArchived, users = [], user }) => {
  const isCreator = task.createdBy?._id === user?._id || task.createdBy === user?._id;
  const [newTitle, setNewTitle]   = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [adding,   setAdding]     = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const subtasks = task.subtasks || [];
  const total    = subtasks.length;
  const completed= subtasks.filter(s => s.completed).length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleToggle = async (sub) => {
    const isMainAssignee = task.assignedTo?.some(u => (u._id || u) === user?._id);
    const isSubAssignee = sub.assignedTo?._id === user?._id || sub.assignedTo === user?._id;
    const canToggle = isAdmin || isCreator || isSubAssignee || (!sub.assignedTo && isMainAssignee);

    if (!canToggle) {
      toast.error('You can only toggle subtasks assigned to you or if you own the task.');
      return;
    }

    setTogglingId(sub._id);
    try {
      const { data } = await taskAPI.updateSubtask(task._id, sub._id, { completed: !sub.completed });
      setTask(t => ({ ...t, subtasks: data.data.subtasks }));
    } catch { toast.error('Failed to update subtask'); }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (subId) => {
    setDeletingId(subId);
    try {
      const { data } = await taskAPI.deleteSubtask(task._id, subId);
      setTask(t => ({ ...t, subtasks: data.data.subtasks }));
      toast.success('Subtask deleted');
    } catch { toast.error('Failed to delete subtask'); }
    finally { setDeletingId(null); }
  };

  const handleAdd = async () => {
    if (!isCreator && !isAdmin) {
      toast.error('Only the task owner or admin can add subtasks');
      return;
    }
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const payload = { title: newTitle.trim() };
      if (newAssignee) payload.assignedTo = newAssignee;
      if (newDueDate) payload.dueDate = new Date(newDueDate).toISOString();
      const { data } = await taskAPI.addSubtask(task._id, payload);
      setTask(t => ({ ...t, subtasks: data.data.subtasks }));
      setNewTitle('');
      setNewAssignee('');
      setNewDueDate('');
    } catch { toast.error('Failed to add subtask'); }
    finally { setAdding(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Subtasks ({completed}/{total})
        </p>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 mb-3">
          <div
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#10b981' : '#6366f1' }}
          />
        </div>
      )}

      {/* Subtask list */}
      {subtasks.length > 0 && (
        <div className="border border-slate-100 dark:border-slate-800 rounded-xl divide-y divide-slate-50 dark:divide-slate-800 mb-3">
          {subtasks.map(sub => (
            <div key={sub._id} className="flex items-center gap-3 px-3 py-2.5">
              {/* Checkbox */}
              <button
                onClick={() => !isArchived && handleToggle(sub)}
                disabled={togglingId === sub._id || isArchived}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  sub.completed
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'border-slate-300 dark:border-slate-600 hover:border-brand-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}>
                {sub.completed && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <p className={`flex-1 text-sm ${sub.completed ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>
                {sub.title}
                {sub.dueDate && (
                  <span className="ml-2 text-xs bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded">
                    Due: {formatDate(sub.dueDate)}
                  </span>
                )}
                {sub.assignedTo && (
                  <span className="ml-1 text-xs bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 px-1.5 py-0.5 rounded font-medium">
                    {sub.assignedTo.name || sub.assignedTo}
                  </span>
                )}
              </p>
              {(isAdmin || isCreator) && !isArchived && (
                <button onClick={() => handleDelete(sub._id)} disabled={deletingId === sub._id}
                  className="text-slate-300 hover:text-red-500 transition-colors font-bold text-base leading-none disabled:opacity-50 flex items-center justify-center p-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add subtask inline */}
      {!isArchived && (isAdmin || isCreator) && (
        <div className="space-y-2 mt-3">
          <div className="flex gap-2">
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }}}
              placeholder="Add subtask title..."
              className="flex-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-brand-500 outline-none"
            />
            <Button type="button" size="sm" variant="secondary" onClick={handleAdd} loading={adding} className="px-3">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </Button>
          </div>
          <div className="flex gap-2">
            <select
              value={newAssignee}
              onChange={(e) => setNewAssignee(e.target.value)}
              className="flex-1 px-2.5 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 outline-none cursor-pointer focus:border-brand-500"
            >
              <option value="">Assignee (Optional)</option>
              {users.map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="flex-1 px-2.5 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-350 outline-none cursor-pointer focus:border-brand-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const AttachmentsSection = ({ task, setTask, user, isAdmin, isArchived }) => {
  const [uploading, setUploading] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);

  const attachments = task.attachments || [];
  const creatorId = task.createdBy?._id?.toString() || task.createdBy?.toString();

  // Separate attachments into Creator Requirements vs Assignee Work Submissions
  const requirementAtts = attachments.filter(att => {
    const uploaderId = att.uploadedBy?._id?.toString() || att.uploadedBy?.toString();
    return uploaderId === creatorId;
  });

  const submissionAtts = attachments.filter(att => {
    const uploaderId = att.uploadedBy?._id?.toString() || att.uploadedBy?.toString();
    return uploaderId !== creatorId;
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const { data } = await uploadAPI.uploadTaskAttachments(formData);
      const uploadedFiles = data.data.attachments || [];

      for (const att of uploadedFiles) {
        const { data: resData } = await taskAPI.addAttachment(task._id, att);
        setTask(t => ({ ...t, attachments: resData.data.attachments }));
      }
      toast.success(`${uploadedFiles.length} file(s) attached to task`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to attach file(s)');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (attId) => {
    try {
      const { data } = await taskAPI.deleteAttachment(task._id, attId);
      setTask(t => ({ ...t, attachments: data.data.attachments }));
      toast.success('Attachment deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const renderFileCard = (att, isRequirementSection) => {
    const isImg = att.fileType?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(att.originalName || '');
    const currentUserId = user?._id?.toString();
    const uploaderId = att.uploadedBy?._id?.toString() || att.uploadedBy?.toString();
    const isUploader = currentUserId && uploaderId === currentUserId;
    const isCreator = currentUserId && creatorId === currentUserId;

    // Strict Permission: Read-only if archived! Assignees CANNOT delete requirement attachments!
    const canDelete = !isArchived && (isAdmin || isCreator || (isUploader && !isRequirementSection));

    return (
      <div key={att._id} className="p-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-900/50 border border-slate-200/80 dark:border-slate-800/80 hover:border-brand-300 dark:hover:border-brand-700 transition-all flex flex-col justify-between group relative shadow-xs">
        <div className="flex items-start gap-2.5 mb-2">
          {isImg ? (
            <img
              src={resolveFileUrl(att.fileUrl)}
              alt={att.originalName}
              onClick={() => setPreviewModal(att)}
              className="w-11 h-11 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0 border border-slate-200/60 dark:border-slate-700/60"
            />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-indigo-50 dark:bg-indigo-950/80 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-900/50">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p
              className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate cursor-pointer hover:text-brand-600"
              onClick={() => isImg ? setPreviewModal(att) : window.open(resolveFileUrl(att.fileUrl), '_blank')}
              title={att.originalName}
            >
              {att.originalName}
            </p>
            {att.fileSize > 0 && <p className="text-[10px] text-slate-400 font-medium">{(att.fileSize / 1024).toFixed(0)} KB</p>}
            <p className="text-[10px] text-slate-400 truncate">
              {att.uploadedBy?.name ? `By ${att.uploadedBy.name}` : 'Uploaded file'}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1.5 border-t border-slate-200/50 dark:border-slate-800/50">
          <button
            type="button"
            onClick={() => handleDownloadFile(att.fileUrl, att.originalName)}
            className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download</span>
          </button>

          {canDelete && (
            <button
              onClick={() => handleDelete(att._id)}
              className="text-[11px] font-bold text-red-500 hover:text-red-700 opacity-60 hover:opacity-100 transition-opacity flex items-center gap-0.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Bar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Attachments &amp; Files ({attachments.length})
          </p>
        </div>
        {!isArchived && (
          <label className={`cursor-pointer px-3 py-1 text-xs font-bold text-brand-600 dark:text-brand-400 bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/40 dark:hover:bg-brand-900/40 rounded-xl transition-all border border-brand-200/60 dark:border-brand-800/40 flex items-center gap-1.5 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input type="file" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{uploading ? 'Uploading...' : 'Upload Attachment'}</span>
          </label>
        )}
      </div>

      {attachments.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
          {isArchived ? 'No attachments were uploaded for this task.' : 'No attachments uploaded yet. Click "Upload Attachment" to add screenshots or reference PDFs.'}
        </p>
      ) : (
        <div className="space-y-4">
          {/* Isolated Group 1: Task Requirements & Reference Files (Uploaded by Task Creator/Admin) */}
          {requirementAtts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Requirement &amp; Reference Files
                </span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold">
                  {requirementAtts.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {requirementAtts.map(att => renderFileCard(att, true))}
              </div>
            </div>
          )}

          {/* Isolated Group 2: Work Proofs & Screenshots (Uploaded by Assignees / Employees) */}
          {submissionAtts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Work Proof &amp; Error Screenshots
                </span>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 text-[10px] font-bold">
                  {submissionAtts.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {submissionAtts.map(att => renderFileCard(att, false))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Image Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4" onClick={() => setPreviewModal(null)}>
          <div className="relative max-w-3xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 border-b border-slate-100 dark:border-slate-800 pb-2">
              <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{previewModal.originalName}</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleDownloadFile(previewModal.fileUrl, previewModal.originalName)}
                  className="px-3.5 py-1.5 bg-brand-600 text-white rounded-xl text-xs font-bold hover:bg-brand-700 flex items-center gap-1.5 shadow-sm transition-all border-0 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>Download</span>
                </button>
                <button onClick={() => setPreviewModal(null)} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center">
              <img src={resolveFileUrl(previewModal.fileUrl)} alt={previewModal.originalName} className="max-w-full max-h-[75vh] rounded-xl object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VoiceNotesSection = ({ task, setTask, user, isAdmin, isArchived }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const timerRef = React.useRef(null);
  const recordingTimeRef = React.useRef(0);

  const voiceNotes = task.voiceNotes || [];
  const creatorId = task.createdBy?._id?.toString() || task.createdBy?.toString();

  const creatorNotes = voiceNotes.filter(vn => {
    const rId = vn.recordedBy?._id?.toString() || vn.recordedBy?.toString();
    return rId === creatorId;
  });

  const assigneeNotes = voiceNotes.filter(vn => {
    const rId = vn.recordedBy?._id?.toString() || vn.recordedBy?.toString();
    return rId !== creatorId;
  });

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('mp4') ? '.m4a' : mimeType.includes('ogg') ? '.ogg' : mimeType.includes('wav') ? '.wav' : '.webm';
        const audioBlob = new Blob(chunks, { type: mimeType });
        const file = new File([audioBlob], `voice_note_${Date.now()}${ext}`, { type: mimeType });
        const finalDuration = recordingTimeRef.current || 1;
        await uploadVoiceFile(file, finalDuration);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      recordingTimeRef.current = 0;
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime(recordingTimeRef.current);
      }, 1000);
    } catch (err) {
      toast.error('Microphone access denied or not supported');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const uploadVoiceFile = async (file, duration) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data } = await uploadAPI.uploadTaskAttachments(formData);
      const uploaded = data.data.attachments?.[0];

      if (uploaded) {
        const { data: resData } = await taskAPI.addVoiceNote(task._id, {
          fileUrl: uploaded.fileUrl,
          fileName: uploaded.originalName || 'voice_note.mp3',
          duration,
          fileSize: uploaded.fileSize
        });
        setTask(t => ({
          ...t,
          voiceNotes: resData.data.voiceNotes,
          activityLog: resData.data.activityLog || t.activityLog
        }));
        toast.success('Voice note added');
      }
    } catch (err) {
      toast.error('Failed to upload voice note');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (vnId) => {
    try {
      const { data } = await taskAPI.deleteVoiceNote(task._id, vnId);
      setTask(t => ({
        ...t,
        voiceNotes: data.data.voiceNotes,
        activityLog: data.data.activityLog || t.activityLog
      }));
      toast.success('Voice note deleted');
    } catch (err) {
      toast.error('Failed to delete voice note');
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const renderVoiceCard = (vn, isRequirementSection) => {
    const currentUserId = user?._id?.toString();
    const recorderId = vn.recordedBy?._id?.toString() || vn.recordedBy?.toString();
    const isRecorder = currentUserId && recorderId === currentUserId;
    const isCreator = currentUserId && creatorId === currentUserId;
    const canDelete = !isArchived && (isAdmin || isCreator || (isRecorder && !isRequirementSection));

    return (
      <div key={vn._id} className="p-3 rounded-xl bg-slate-50/90 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 hover:border-brand-300 dark:hover:border-brand-700 transition-all space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar name={vn.recordedBy?.name} src={vn.recordedBy?.avatar} size="xs" />
            <div>
              <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                {vn.recordedBy?.name || 'User'}
              </p>
              <p className="text-[10px] text-slate-400 font-medium">
                {formatDate(vn.recordedAt)} {vn.duration > 0 ? `· ${formatTime(vn.duration)}` : ''}
              </p>
            </div>
          </div>
          {canDelete && (
            <button onClick={() => handleDelete(vn._id)} className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
        <audio controls src={resolveFileUrl(vn.fileUrl)} className="w-full h-8 rounded-lg outline-none" />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Voice Notes ({voiceNotes.length})
          </p>
        </div>

        {/* Record Control Button */}
        {!isArchived && (
          isRecording ? (
            <button
              onClick={stopRecording}
              className="px-3 py-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all flex items-center gap-1.5 shadow-sm animate-pulse"
            >
              <span className="w-2 h-2 rounded-full bg-white"></span>
              <span>Stop Recording ({formatTime(recordingTime)})</span>
            </button>
          ) : (
            <button
              onClick={startRecording}
              disabled={uploading}
              className={`px-3 py-1 text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:hover:bg-red-900/40 rounded-xl transition-all border border-red-200/60 dark:border-red-800/40 flex items-center gap-1.5 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              <span>{uploading ? 'Uploading...' : 'Record Voice Note'}</span>
            </button>
          )
        )}
      </div>

      {voiceNotes.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
          {isArchived ? 'No voice notes were recorded for this task.' : 'No voice notes recorded yet. Click "Record Voice Note" to send an audio message.'}
        </p>
      ) : (
        <div className="space-y-4">
          {/* Isolated Group 1: Task Creator Audio Instructions */}
          {creatorNotes.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Task Audio Instructions
                </span>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-bold">
                  {creatorNotes.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {creatorNotes.map(vn => renderVoiceCard(vn, true))}
              </div>
            </div>
          )}

          {/* Isolated Group 2: Assignee Voice Updates */}
          {assigneeNotes.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Assignee Work Audio Updates
                </span>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 text-[10px] font-bold">
                  {assigneeNotes.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {assigneeNotes.map(vn => renderVoiceCard(vn, false))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ActivityTimeline = ({ task }) => {
  const activityLog = task.activityLog || [];

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />;
      case 'status_changed':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />;
      case 'priority_changed':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />;
      case 'deadline_changed':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />;
      case 'title_changed':
      case 'description_changed':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />;
      case 'assignees_changed':
      case 'reassigned':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />;
      case 'voice_note_added':
      case 'voice_note_deleted':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />;
      case 'attachment_added':
      case 'attachment_deleted':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />;
      case 'subtask_toggled':
      case 'subtask_added':
      case 'subtask_deleted':
      case 'subtask_completed':
      case 'subtask_reopened':
      case 'subtasks_updated':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'comment_added':
      case 'comment_deleted':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />;
      default:
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;
    }
  };

  return (
    <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Activity Audit Trail ({activityLog.length})</span>
        </p>
      </div>

      {activityLog.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
          No activity logs recorded yet.
        </p>
      ) : (
        <div className="relative pl-6 space-y-5 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
          {activityLog.map((act) => (
            <div key={act._id} className="relative flex items-start gap-3 group">
              <div className="absolute -left-6 top-0.5 w-5 h-5 rounded-full bg-white dark:bg-slate-900 border-2 border-brand-500 flex items-center justify-center text-brand-600 dark:text-brand-400 shadow-xs">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {getActionIcon(act.action)}
                </svg>
              </div>

              <div className="flex-1 bg-slate-50/80 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/60 p-3 rounded-xl">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5">
                    <Avatar name={act.performedBy?.name} src={act.performedBy?.avatar} size="xs" />
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{act.performedBy?.name || 'User'}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">{formatDate(act.timestamp)}</span>
                </div>
                <p className="text-xs font-semibold text-brand-600 dark:text-brand-400">{act.title}</p>
                {act.details && <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{act.details}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TaskDetail = ({ taskId, onClose, onUpdated }) => {
  const { user, isAdmin, isManager } = useAuth();
  const [task,           setTask]          = useState(null);
  const [loading,        setLoading]       = useState(true);
  const [comment,        setComment]       = useState('');
  const [submitting,     setSubmitting]    = useState(false);
  const [approving,      setApproving]     = useState(false);
  const [activeTab,      setActiveTab]     = useState('details');
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [newDeadline,    setNewDeadline]   = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [reasonModal,    setReasonModal]   = useState({ open:false, type:null, pendingStatus:null });
  const [confirmModal,   setConfirmModal]  = useState({ open:false, title:'', message:'', confirmText:'Confirm', confirmVariant:'primary', onConfirm:null });
  const [users,          setUsers]         = useState([]);
  const [reopening,      setReopening]     = useState(false);
  const [pinging,        setPinging]       = useState(false);
  const [cooldownSecs,   setCooldownSecs]  = useState(0);

  const [selectedMentions, setSelectedMentions] = useState([]);
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  
  const [isCommentRecording, setIsCommentRecording] = useState(false);
  const [commentRecordTime, setCommentRecordTime] = useState(0);
  const [commentMediaRecorder, setCommentMediaRecorder] = useState(null);
  const commentTimerRef = React.useRef(null);
  const commentRecordTimeRef = React.useRef(0);

  const playChimeSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.35);
    } catch (e) {}
  }, []);

  const fetchTask = useCallback(() => {
    setLoading(true);
    taskAPI.getById(taskId)
      .then(({ data }) => setTask(data.data.task))
      .catch(() => toast.error('Failed to load task'))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    if (!task?.lastPingedAt) {
      setCooldownSecs(0);
      return;
    }
    const updateCooldown = () => {
      const COOLDOWN_MS = 15 * 60 * 1000;
      const diff = Date.now() - new Date(task.lastPingedAt).getTime();
      const remaining = Math.max(0, Math.ceil((COOLDOWN_MS - diff) / 1000));
      setCooldownSecs(remaining);
    };
    updateCooldown();
    const timer = setInterval(updateCooldown, 1000);
    return () => clearInterval(timer);
  }, [task?.lastPingedAt]);

  const handlePing = async () => {
    if (!isAdmin && !isCreator) {
      toast.error('Only Admin or Task Creator can ping assignees');
      return;
    }
    if (!task.assignedTo || task.assignedTo.length === 0) {
      toast.error('No assignees to ping on this task');
      return;
    }
    if (cooldownSecs > 0) {
      const mins = Math.ceil(cooldownSecs / 60);
      toast.error(`Ping cooldown active. Try again in ${mins} minute(s).`);
      return;
    }

    setPinging(true);
    try {
      const { data } = await taskAPI.ping(task._id);
      playChimeSound();
      toast.success(data.message || 'Ping sent to assignees!');
      setTask(t => ({ ...t, lastPingedAt: data.data.lastPingedAt, pingCount: data.data.pingCount }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to ping assignees');
    } finally {
      setPinging(false);
    }
  };

  useEffect(() => { if (taskId) fetchTask(); }, [fetchTask, taskId]);

  useEffect(() => {
    if (user) {
      fetchUsersCached(userAPI, user, { limit: 100 })
        .then((userList) => setUsers(userList || []))
        .catch(() => {});
    }
  }, [user]);

  const isArchived = task?.isArchived || false;
  const isCreator = task?.createdBy?._id === user?._id || task?.createdBy === user?._id;
  const isAssignee = task?.assignedTo?.some(u => (u._id || u) === user?._id);
  const canChangeStatus = isAdmin || isCreator || isAssignee;
  const canEdit = isAdmin || isCreator;
  const canRequestReopen = !isAdmin && (isAssignee || isCreator);

  const hasManagerApproval = task?.managerApproval && task.managerApproval.status !== 'not_applicable';
  const isManagerPending = hasManagerApproval && task?.managerApproval?.status === 'pending';
  const isAdminPending = task?.adminApproval?.status !== 'approved';

  const isTaskManager = Boolean(isManager && (
    (task?.departmentId && (user?.managedDepartments || []).some(d => d._id?.toString() === (task.departmentId?._id || task.departmentId)?.toString())) ||
    (task?.createdBy?.departmentId && (user?.managedDepartments || []).some(d => d._id?.toString() === (task.createdBy?.departmentId?._id || task.createdBy?.departmentId)?.toString())) ||
    (task?.departmentId?.managerId && (task.departmentId.managerId._id || task.departmentId.managerId)?.toString() === user?._id?.toString())
  ));

  const canDirectReopen = isAdmin || isTaskManager;

  const handleStatusChange = async (newStatus) => {
    if (newStatus === task.status) return;
    if (newStatus === '__reopen__') {
      handleDirectReopen();
      return;
    }
    if (newStatus === 'on_hold') {
      setReasonModal({ open:true, type:'on_hold', pendingStatus: newStatus });
      return;
    }
    try {
      const { data } = await taskAPI.update(taskId, { status: newStatus });
      setTask(data.data.task); onUpdated?.();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update status'); }
  };

  const confirmHoldReason = async (reason) => {
    try {
      const { data } = await taskAPI.update(taskId, { status: reasonModal.pendingStatus, holdReason: reason });
      setTask(data.data.task); onUpdated?.();
      setReasonModal({ open:false, type:null, pendingStatus:null });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update'); }
  };

  const startEditDeadline = () => {
    setNewDeadline(task.deadline ? task.deadline.slice(0,10) : '');
    setEditingDeadline(true);
  };

  const handleDeadlineSave = () => {
    const current = task.deadline ? task.deadline.slice(0,10) : '';
    if (newDeadline === current) { setEditingDeadline(false); return; }
    setReasonModal({ open:true, type:'deadline', pendingStatus:null });
  };

  const confirmDeadlineReason = async (reason) => {
    setSavingDeadline(true);
    try {
      if (isAdmin || isCreator) {
        const { data } = await taskAPI.update(taskId, {
          deadline:       newDeadline ? new Date(newDeadline).toISOString() : null,
          deadlineReason: reason,
        });
        setTask(data.data.task); onUpdated?.();
        toast.success('Deadline updated');
      } else {
        await deadlineExtensionAPI.request(taskId, {
          proposedDeadline: newDeadline,
          reason,
        });
        toast.success('Deadline extension request sent to Admin');
      }
      setEditingDeadline(false);
      setReasonModal({ open:false, type:null, pendingStatus:null });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit request'); }
    finally { setSavingDeadline(false); }
  };

  const confirmReopenReason = async (reason) => {
    try {
      await reopenAPI.request(taskId, { reason });
      toast.success('Reopen request submitted');
      setReasonModal({ open:false, type:null, pendingStatus:null });
      fetchTask();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    }
  };

  const handlePriorityChange = async (p) => {
    try {
      const { data } = await taskAPI.update(taskId, { priority: p });
      setTask(data.data.task); onUpdated?.();
    } catch { toast.error('Failed to update priority'); }
  };

  const handleApprove = (type = 'admin') => {
    const isManagerAction = type === 'manager';
    const isOnBehalf = isAdmin && isManagerAction;

    let modalTitle = 'Approve & Close Task';
    let modalMsg = 'Are you sure you want to approve and archive this task?';
    let confirmBtnText = 'Approve & Close';

    if (isOnBehalf) {
      modalTitle = 'Approve on Behalf of Manager';
      modalMsg = 'Are you sure you want to approve this task on behalf of the Department Manager?';
      confirmBtnText = 'Approve as Manager';
    } else if (isManagerAction) {
      modalTitle = 'Approve Task';
      modalMsg = 'Are you sure you want to approve this completed task for your department? (It will remain in the task list until Admin approves)';
      confirmBtnText = 'Approve Task';
    } else if (isAdmin) {
      modalTitle = 'Approve & Archive Task';
      modalMsg = 'Are you sure you want to approve and archive this task? (It will be moved to the Archive page)';
      confirmBtnText = 'Approve & Archive';
    }

    setConfirmModal({
      open: true,
      title: modalTitle,
      message: modalMsg,
      confirmText: confirmBtnText,
      confirmVariant: 'success',
      onConfirm: async () => {
        setApproving(true);
        try {
          const { data } = await taskAPI.approve(taskId, { approvalType: type });
          setTask(data.data.task);
          toast.success('Task approved successfully');
          onUpdated?.();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to approve'); }
        finally { setApproving(false); setConfirmModal(prev => ({ ...prev, open: false })); }
      }
    });
  };

  const handleDirectReopen = () => {
    setConfirmModal({
      open: true,
      title: 'Reopen Task',
      message: 'Are you sure you want to reopen this task? It will return to To-Do and assignees will be notified.',
      confirmText: 'Reopen Task',
      confirmVariant: 'warning',
      onConfirm: async () => {
        setReopening(true);
        try {
          const { data } = await taskAPI.directReopen(taskId);
          setTask(data.data.task);
          toast.success('Task reopened successfully');
          onUpdated?.();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to reopen task'); }
        finally { setReopening(false); setConfirmModal(prev => ({ ...prev, open: false })); }
      }
    });
  };

  const handleCommentChange = (e) => {
    const val = e.target.value;
    setComment(val);
    const lastAtIndex = val.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = val.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionQuery(textAfterAt.toLowerCase());
        setShowMentionPopup(true);
        return;
      }
    }
    setShowMentionPopup(false);
  };

  const handleSelectMentionUser = (u) => {
    const lastAtIndex = comment.lastIndexOf('@');
    const newText = comment.slice(0, lastAtIndex) + `@${u.name} `;
    setComment(newText);
    if (!selectedMentions.includes(u._id)) {
      setSelectedMentions(prev => [...prev, u._id]);
    }
    setShowMentionPopup(false);
  };

  const startCommentRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('mp4') ? '.m4a' : mimeType.includes('ogg') ? '.ogg' : mimeType.includes('wav') ? '.wav' : '.webm';
        const audioBlob = new Blob(chunks, { type: mimeType });
        const file = new File([audioBlob], `voice_comment_${Date.now()}${ext}`, { type: mimeType });
        const finalDuration = commentRecordTimeRef.current || 1;
        await postVoiceComment(file, finalDuration);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      setCommentMediaRecorder(recorder);
      setIsCommentRecording(true);
      commentRecordTimeRef.current = 0;
      setCommentRecordTime(0);
      commentTimerRef.current = setInterval(() => {
        commentRecordTimeRef.current += 1;
        setCommentRecordTime(commentRecordTimeRef.current);
      }, 1000);
    } catch {
      toast.error('Microphone access denied or not supported');
    }
  };

  const stopCommentRecording = () => {
    if (commentMediaRecorder && isCommentRecording) {
      commentMediaRecorder.stop();
      setIsCommentRecording(false);
      if (commentTimerRef.current) clearInterval(commentTimerRef.current);
    }
  };

  const postVoiceComment = async (file, duration) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data: uploadRes } = await uploadAPI.uploadTaskAttachments(formData);
      const uploaded = uploadRes.data.attachments?.[0];
      if (uploaded) {
        const voiceNotePayload = {
          fileUrl: uploaded.fileUrl,
          fileName: uploaded.originalName || 'voice_comment.mp3',
          duration,
        };
        const { data } = await taskAPI.addComment(taskId, {
          text: comment.trim() || 'Voice Comment',
          mentions: selectedMentions,
          voiceNote: voiceNotePayload,
        });
        setTask(t => ({ ...t, comments: data.data.comments }));
        setComment('');
        setSelectedMentions([]);
        toast.success('Voice comment posted!');
      }
    } catch (err) {
      toast.error('Failed to post voice comment');
    } finally {
      setSubmitting(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await taskAPI.addComment(taskId, {
        text: comment,
        mentions: selectedMentions
      });
      setTask((t) => ({ ...t, comments: data.data.comments }));
      setComment('');
      setSelectedMentions([]);
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      const { data } = await taskAPI.deleteComment(taskId, commentId);
      setTask((t) => ({ ...t, comments: data.data.comments }));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete comment');
    }
  };

  if (!taskId) return null;

  const tabClass = (t) =>
    `px-4 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 border ${
      activeTab===t
        ? 'bg-white dark:bg-slate-800 text-brand-600 dark:text-brand-400 shadow-sm border-slate-200/30 dark:border-slate-700/40'
        : 'text-slate-500 dark:text-slate-400 border-transparent hover:bg-slate-100/60 dark:hover:bg-slate-800/40 hover:text-brand-600 dark:hover:text-slate-350'
    }`;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[4px]" onClick={onClose}/>
      <div className="relative w-full max-w-2xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl flex flex-col animate-slide-up overflow-hidden border-l border-white/20 dark:border-slate-800/40">
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Spinner size="lg"/></div>
        ) : task ? (
          <>
            {/* Archived Informative Banner — Compact Strip */}
            {isArchived && (
              <div className="bg-amber-50 dark:bg-amber-950/60 border-b border-amber-200/80 dark:border-amber-900/60 px-5 py-2 flex items-center justify-between text-xs text-amber-900 dark:text-amber-200 flex-shrink-0">
                <div className="flex items-center gap-2 font-medium">
                  <svg className="w-4 h-4 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                  </svg>
                  <span className="font-bold">Archived Task</span>
                  <span className="text-amber-400">·</span>
                  <span className="text-amber-700 dark:text-amber-400 text-[11px]">Approved by Manager & Admin · Stored in archives</span>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-200/90 dark:bg-amber-900 text-amber-900 dark:text-amber-200 flex-shrink-0">
                  Read-Only
                </span>
              </div>
            )}

            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-800/40 bg-gradient-to-br from-brand-50/40 via-white dark:from-brand-950/10 dark:via-slate-900 to-transparent">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge className={STATUS_CONFIG[task.status]?.color} dot={STATUS_CONFIG[task.status]?.dot}>
                      {STATUS_CONFIG[task.status]?.label}
                    </Badge>
                    <Badge className={PRIORITY_CONFIG[task.priority]?.color}>
                      <div className="flex items-center gap-1">
                        <PriorityIcon iconName={PRIORITY_CONFIG[task.priority]?.iconName} className="w-3 h-3" />
                        <span>{PRIORITY_CONFIG[task.priority]?.label}</span>
                      </div>
                    </Badge>
                    {task.isRecurring && (
                      <Badge className="bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
                        <div className="flex items-center gap-1 font-bold">
                          <NavIcon name="ArrowPathIcon" className="w-3 h-3" />
                          <span>Recurring ({task.recurringConfig?.frequency || 'weekly'})</span>
                        </div>
                      </Badge>
                    )}
                    {isArchived && (
                      <Badge className="bg-slate-100 dark:bg-slate-800 text-slate-500">
                        <div className="flex items-center gap-1">
                          <NavIcon name="ArchiveBoxIcon" className="w-3 h-3" />
                          <span>Archived</span>
                        </div>
                      </Badge>
                    )}
                    {isOverdue(task.deadline, task.status) && !isArchived && (
                      <Badge className="bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400">
                        <div className="flex items-center gap-1">
                          <NavIcon name="ExclamationTriangleIcon" className="w-3 h-3" />
                          <span>Overdue</span>
                        </div>
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white font-display leading-snug">{task.title}</h2>
                </div>
                <div className="flex items-center gap-2">
                  {/* Ping Assignees Button (Admin / Creator only, non-completed tasks) */}
                  {(isAdmin || isCreator) && task.status !== 'completed' && !isArchived && (
                    <button
                      onClick={handlePing}
                      disabled={pinging || cooldownSecs > 0}
                      className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all flex items-center gap-1.5 shadow-sm active:scale-95 ${
                        cooldownSecs > 0
                          ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-300/50 dark:border-amber-800/60 opacity-60 cursor-not-allowed'
                          : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25 border border-amber-400/40'
                      }`}
                      title={cooldownSecs > 0 ? `Next ping available in ${Math.floor(cooldownSecs/60)}m ${cooldownSecs%60}s` : 'Send urgent ping notification to assignees'}
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      <span>
                        {pinging
                          ? 'Pinging...'
                          : cooldownSecs > 0
                          ? `Pinged (${Math.floor(cooldownSecs / 60)}:${(cooldownSecs % 60).toString().padStart(2, '0')})`
                          : 'Ping Assignees'}
                      </span>
                      {task.pingCount > 0 && (
                        <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-mono font-bold ${cooldownSecs > 0 ? 'bg-amber-200/50 text-amber-800 dark:text-amber-300' : 'bg-white/25 text-white'}`}>
                          {task.pingCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-all flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>

              {/* Approval status indicators (when completed or archived) */}
              {(task.status === 'completed' || isArchived) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {hasManagerApproval && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                      task.managerApproval?.status === 'approved'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800'
                        : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800'
                    }`}>
                      {task.managerApproval?.status === 'approved' ? (
                        <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      )}
                      <span>Manager: {task.managerApproval?.status === 'approved' ? 'Approved' : 'Pending'}</span>
                      {task.managerApproval?.status === 'approved' && task.managerApproval.approvedBy && (
                        <span className="opacity-75 font-normal">
                          ({task.managerApproval.approvedBy.name || 'Mgr'}{task.managerApproval.onBehalfOfManager ? ' by Admin' : ''})
                        </span>
                      )}
                    </span>
                  )}

                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                    task.adminApproval?.status === 'approved'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800'
                      : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800'
                  }`}>
                    {task.adminApproval?.status === 'approved' ? (
                      <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                    )}
                    <span>Admin: {task.adminApproval?.status === 'approved' ? 'Approved' : 'Pending'}</span>
                    {task.adminApproval?.status === 'approved' && task.adminApproval.approvedBy && (
                      <span className="opacity-75 font-normal">
                        ({task.adminApproval.approvedBy.name || 'Admin'})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Action Buttons: Approvals & Reopen */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {/* Manager Approve button */}
                {isTaskManager && isManagerPending && task.status === 'completed' && !isArchived && (
                  <Button variant="success" size="sm" loading={approving} onClick={() => handleApprove('manager')} className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Approve Task</span>
                  </Button>
                )}

                {/* Admin: Approve by Admin */}
                {isAdmin && isAdminPending && task.status === 'completed' && !isArchived && (
                  <Button variant="success" size="sm" loading={approving} onClick={() => handleApprove('admin')} className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Approve by Admin</span>
                  </Button>
                )}

                {/* Admin: Approve on Behalf of Manager (if task has manager approval pending) */}
                {isAdmin && isManagerPending && task.status === 'completed' && !isArchived && (
                  <Button variant="secondary" size="sm" loading={approving} onClick={() => handleApprove('manager')} className="flex items-center gap-1.5 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Approve on Behalf of Manager</span>
                  </Button>
                )}




                {/* Request Reopen — user/assignee (non-admin, non-manager), completed, not pending, not archived */}
                {canRequestReopen && !canDirectReopen && task.status === 'completed' && !task.reopenPending && !isArchived && (
                  <Button variant="primary" size="sm" onClick={() => setReasonModal({ open: true, type: 'reopen', pendingStatus: null })} className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89" />
                    </svg>
                    <span>Request Reopen</span>
                  </Button>
                )}
              </div>

              {/* Reopen Request Pending Banner — only visible to normal user/member, not admin or manager */}
              {!isAdmin && !isTaskManager && task.reopenPending && (
                <div className="mt-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center gap-2 justify-center">
                  <NavIcon name="ClockIcon" className="w-5 h-5 text-amber-500 animate-pulse" />
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                    Reopen Request Pending Approval
                  </p>
                </div>
              )}
            </div>

            {/* Status & Priority Row — only shown for active (non-archived) tasks */}
            {!isArchived && (
              <div className="px-6 py-3 bg-slate-50/80 dark:bg-slate-800/30 border-b border-slate-100/80 dark:border-slate-800/40 flex gap-5 flex-wrap backdrop-blur-sm">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status</p>
                  <InlineSelect
                    value={task.status}
                    disabled={!canChangeStatus && !canDirectReopen}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    {(task.status === 'reopened'
                      ? ['reopened', 'todo', 'in_progress', 'completed', 'on_hold']
                      : ['todo', 'in_progress', 'completed', 'on_hold']
                    ).map((s) => (
                      <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>
                    ))}
                    {canDirectReopen && task.status === 'completed' && (
                      <option value="__reopen__" style={{ color: '#d97706', fontWeight: 600 }}>
                        {reopening ? 'Reopening…' : '↩ Reopen Task'}
                      </option>
                    )}
                  </InlineSelect>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Priority</p>
                  <InlineSelect value={task.priority} disabled={!canEdit} onChange={(e) => handlePriorityChange(e.target.value)}>
                    {['low','medium','high','critical'].map((p) => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>
                    ))}
                  </InlineSelect>
                </div>
              </div>
            )}

            {/* Tab bar — Centered */}
            <div className="px-6 pt-3 pb-3 flex gap-2 border-b border-slate-100/80 dark:border-slate-800/40 justify-center flex-wrap backdrop-blur-sm">
              {['details','activity','status','deadline','reopen'].map((t) => {
                const reopenCount = task.statusHistory?.filter(e => e.isReopen)?.length || 0;
                return (
                  <button key={t} className={tabClass(t)} onClick={() => setActiveTab(t)}>
                    {t === 'reopen' ? 'Reopen History' : t === 'activity' ? 'Activity Log' : t.charAt(0).toUpperCase()+t.slice(1)}
                    {t==='activity' && task.activityLog?.length     > 0 && <span className="ml-1 opacity-60">({task.activityLog.length})</span>}
                    {t==='status'   && task.statusHistory?.length   > 0 && <span className="ml-1 opacity-60">({task.statusHistory.length})</span>}
                    {t==='deadline' && task.deadlineHistory?.length > 0 && <span className="ml-1 opacity-60">({task.deadlineHistory.length})</span>}
                    {t==='reopen'   && reopenCount > 0                   && <span className="ml-1 opacity-60">({reopenCount})</span>}
                  </button>
                );
              })}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-950/20">

              {/* ── Details tab ── */}
              {activeTab === 'details' && (
                <div className="px-6 py-5 space-y-4">
                  {/* Description Card */}
                  {task.description && (
                    <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                      <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Description</p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">{task.description}</p>
                    </div>
                  )}

                  {/* Task Key Metadata Card */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Task Info</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold text-slate-400">Deadline</p>
                          {!isArchived && (isAdmin || isCreator || isAssignee) && (
                            <button onClick={startEditDeadline} className="text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 rounded-full bg-brand-50 hover:bg-brand-100 text-brand-600 dark:bg-brand-950/40 dark:text-brand-400 dark:hover:bg-brand-900/40 transition-all border border-brand-100/60 dark:border-brand-800/30">
                              {isAdmin || isCreator ? 'Edit' : 'Request Extension'}
                            </button>
                          )}
                        </div>
                        {editingDeadline ? (
                          <div className="space-y-2">
                            <input type="datetime-local" value={newDeadline} onChange={(e) => setNewDeadline(e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:border-brand-500 outline-none"/>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={handleDeadlineSave} loading={savingDeadline}>Save</Button>
                              <Button size="sm" variant="secondary" onClick={() => setEditingDeadline(false)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-sm font-semibold ${isOverdue(task.deadline,task.status) && !isArchived ?'text-red-600 dark:text-red-400':'text-slate-800 dark:text-slate-200'}`}>
                            {formatDateTime(task.deadline)}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Department</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{task.departmentId?.name || '—'}</p>
                      </div>
                      {task.groupId && (
                        <div>
                          <p className="text-xs font-semibold text-slate-400 mb-1">Group</p>
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{task.groupId?.name || '—'}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Created by</p>
                        <div className="flex items-center gap-1.5">
                          <Avatar name={task.createdBy?.name} src={task.createdBy?.avatar} size="xs"/>
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{task.createdBy?.name}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 mb-1">Created</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{formatDate(task.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Assignees & Tags Card */}
                  {(task.assignedTo?.length > 0 || task.tags?.length > 0) && (
                    <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm space-y-4">
                      {task.assignedTo?.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2.5">Assigned to ({task.assignedTo.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {task.assignedTo.map((u) => (
                              <div key={u._id} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700/50 rounded-xl px-3 py-1.5">
                                <Avatar name={u.name} src={u.avatar} size="xs"/>
                                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">{u.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {task.tags?.length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Tags</p>
                          <div className="flex flex-wrap gap-1.5">
                            {task.tags.map((t) => <span key={t} className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full text-xs font-semibold">{t}</span>)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Inline Subtasks Checklist Card */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <SubtasksSection task={task} setTask={setTask} isAdmin={isAdmin} isArchived={isArchived} users={users} user={user}/>
                  </div>

                  {/* Task File Attachments & Screenshots Card */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <AttachmentsSection task={task} setTask={setTask} user={user} isAdmin={isAdmin} isArchived={isArchived} />
                  </div>

                  {/* Task Voice Notes Audio Recording Card */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <VoiceNotesSection task={task} setTask={setTask} user={user} isAdmin={isAdmin} isArchived={isArchived} />
                  </div>

                  {/* Comments Card */}
                  <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Comments ({task.comments?.length||0})</p>
                    <div className="space-y-3 mb-4">
                      {task.comments?.map((c) => {
                        const canDelete = !isArchived && (c.user?._id === user?._id || user?.role === 'admin' || user?.role === 'superadmin') && !c.isDeleted;
                        return (
                          <div key={c._id} className="flex gap-3 items-start group relative">
                            <Avatar name={c.user?.name} src={c.user?.avatar} size="sm"/>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <div className="flex items-baseline gap-2">
                                  <span className="text-xs font-bold text-slate-900 dark:text-white">{c.user?.name}</span>
                                  <span className="text-[10px] text-slate-400 font-medium">{formatDate(c.createdAt)}</span>
                                </div>
                                {canDelete && (
                                  <button onClick={() => handleDeleteComment(c._id)} title="Delete comment"
                                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 rounded transition-all">
                                    <TrashIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              {c.isDeleted ? (
                                <div className="text-xs italic text-slate-400 dark:text-slate-500 bg-slate-50/50 dark:bg-slate-800/20 border border-slate-100 dark:border-slate-800 rounded-2xl rounded-tl-none px-3.5 py-1.5 mt-0.5 max-w-[95%] flex items-center gap-1.5">
                                  <TrashIcon className="w-3 h-3 text-slate-400" />
                                  <span>This comment was deleted</span>
                                </div>
                              ) : (
                                <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50/80 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 rounded-2xl rounded-tl-none px-3.5 py-2 mt-0.5 max-w-[95%] leading-relaxed space-y-1.5">
                                  <p>{c.text}</p>
                                  {c.voiceNote?.fileUrl && (
                                    <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800">
                                      <audio controls src={resolveFileUrl(c.voiceNote.fileUrl)} className="w-full h-7 outline-none" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {!task.comments?.length && <p className="text-xs text-slate-400 text-center py-4">No comments yet</p>}
                    </div>

                    {/* Comment Form with Mentions Autocomplete & Voice Recording OR Read-only notice */}
                    {isArchived ? (
                      <div className="py-2.5 px-3 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 rounded-xl text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                        </svg>
                        <span>Comments are disabled for archived tasks</span>
                      </div>
                    ) : (
                      <div className="relative">
                        {showMentionPopup && (
                          <div className="absolute bottom-full mb-2 left-10 z-50 w-64 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 divide-y divide-slate-100 dark:divide-slate-700/50">
                            {users.filter(u => u.name?.toLowerCase().includes(mentionQuery)).map(u => (
                              <button
                                key={u._id}
                                type="button"
                                onClick={() => handleSelectMentionUser(u)}
                                className="w-full text-left px-3 py-2 hover:bg-brand-50 dark:hover:bg-slate-700/60 flex items-center gap-2 text-xs transition-colors"
                              >
                                <Avatar name={u.name} src={u.avatar} size="xs" />
                                <div className="flex-1 truncate">
                                  <p className="font-bold text-slate-800 dark:text-slate-200">{u.name}</p>
                                  <p className="text-[10px] text-slate-400">{u.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        <form onSubmit={submitComment} className="flex gap-2 items-center">
                          <Avatar name={user?.name} src={user?.avatar} size="sm"/>
                          <div className="flex-1 flex gap-2 items-center">
                            <input
                              value={comment}
                              onChange={handleCommentChange}
                              placeholder="Add a comment... (Type @ to mention team members)"
                              className="flex-1 px-3.5 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50 dark:bg-slate-950 text-slate-900 dark:text-white focus:border-brand-500 outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-950/20 transition-all"
                            />

                            {/* Mic Voice Comment Button */}
                            {isCommentRecording ? (
                              <button
                                type="button"
                                onClick={stopCommentRecording}
                                className="px-2.5 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all flex items-center gap-1 animate-pulse shadow-xs"
                              >
                                <span className="w-2 h-2 rounded-full bg-white"></span>
                                <span>{commentRecordTime}s</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={startCommentRecording}
                                title="Record Voice Comment"
                                className="p-2 text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-xl border border-slate-200 dark:border-slate-700 transition-all"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                              </button>
                            )}

                            <Button type="submit" size="sm" loading={submitting} disabled={!comment.trim()}>Post</Button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Activity Audit Trail tab ── */}
              {activeTab === 'activity' && (
                <div className="px-6 py-5">
                  <ActivityTimeline task={task} />
                </div>
              )}



              {/* ── Status History tab ── */}
              {activeTab === 'status' && (
                <div className="px-6 py-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Status History ({task.statusHistory?.length||0} events)</p>
                  {task.statusHistory?.length > 0 ? (
                    <div className="relative pl-3 border-l-2 border-slate-100 dark:border-slate-800">
                      {[...task.statusHistory].reverse().map((e, i) => <StatusHistoryRow key={i} entry={e}/>)}
                    </div>
                  ) : <p className="text-sm text-slate-400 text-center py-8">No status changes yet</p>}
                </div>
              )}

              {/* ── Deadline History tab ── */}
              {activeTab === 'deadline' && (
                <div className="px-6 py-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Deadline History ({task.deadlineHistory?.length||0} changes)</p>
                  {task.deadlineHistory?.length > 0 ? (
                    <div className="rounded-xl border border-amber-100 dark:border-amber-950 bg-amber-50 dark:bg-amber-950/30 px-4 py-2">
                      {[...task.deadlineHistory].reverse().map((e, i) => <DeadlineHistoryRow key={i} entry={e}/>)}
                    </div>
                  ) : <p className="text-sm text-slate-400 text-center py-8">Deadline has not been changed</p>}
                </div>
              )}

              {/* ── Reopen History tab ── */}
              {activeTab === 'reopen' && (() => {
                const reopenEntries = [...(task.statusHistory || [])].filter(e => e.isReopen).reverse();
                return (
                  <div className="px-6 py-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                      Reopen History ({reopenEntries.length} {reopenEntries.length === 1 ? 'event' : 'events'})
                    </p>
                    {reopenEntries.length > 0 ? (
                      <div className="space-y-3">
                        {reopenEntries.map((e, i) => {
                          const isManager = e.changedBy?.role === 'manager' || e.reason?.toLowerCase().includes('(manager)');
                          const roleLabel = isManager ? 'Manager' : (e.changedBy?.role === 'superadmin' ? 'Super Admin' : 'Admin');
                          return (
                            <div key={i} className="flex gap-3 items-start p-3.5 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/40">
                              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                  <span className="text-sm font-semibold text-slate-800 dark:text-white">
                                    {e.changedBy?.name || roleLabel}
                                  </span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                                    isManager 
                                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                                      : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                                  }`}>
                                    {roleLabel}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-400 mb-1">{new Date(e.changedAt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</p>
                                {e.reason && (
                                  <p className="text-xs text-slate-600 dark:text-slate-400 italic bg-white/60 dark:bg-slate-800/40 rounded-lg px-2.5 py-1.5 mt-1">
                                    "{e.reason.replace('[REOPEN] ','')}"
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <svg className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89" />
                        </svg>
                        <p className="text-sm text-slate-400">This task has not been reopened yet</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">Task not found</div>
        )}
      </div>

      <ReasonModal
        open={reasonModal.open && reasonModal.type === 'on_hold'}
        onClose={() => setReasonModal({ open:false, type:null, pendingStatus:null })}
        onConfirm={confirmHoldReason}
        title="Reason for Hold"
        placeholder="Why is this task on hold?"
      />
      <ReasonModal
        open={reasonModal.open && reasonModal.type === 'deadline'}
        onClose={() => { setReasonModal({ open:false, type:null, pendingStatus:null }); setEditingDeadline(false); }}
        onConfirm={confirmDeadlineReason}
        title="Reason for Deadline Change"
        placeholder="Why is the deadline being changed?"
        loading={savingDeadline}
      />
      <ReasonModal
        open={reasonModal.open && reasonModal.type === 'reopen'}
        onClose={() => setReasonModal({ open:false, type:null, pendingStatus:null })}
        onConfirm={confirmReopenReason}
        title="Reason for Reopen"
        placeholder="Why do you want to reopen this task?"
      />
      <ConfirmDialog
        open={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmVariant={confirmModal.confirmVariant}
        loading={reopening || approving}
      />
    </div>
  );
};